import React from 'react'
import { JobOfferSummary } from 'javascripts/api/generated'
import styled from 'styled-components'
import GreenWave from 'images/spa/green_wave.svg'
import IconOccupation from 'images/spa/icon_occupation.svg'
import IconArea from 'images/spa/icon_area.svg'
import { useFormatNumber } from 'spa/hooks/useFormatNumber'

export type Props = {
  jobOfferSummary: JobOfferSummary;
}

const Container = styled.a`
  background-color: ${props => props.theme.members.colors.white};
  padding: 1rem;
  width: 100%;
  display: flex;
  gap: 1rem;
  color: ${props => props.theme.members.colors.black._100};

  &:hover {
    color: ${props => props.theme.members.colors.black._100};
    text-decoration: none;
    cursor: pointer;
    opacity: .7;
  }
`

const Image = styled.img`
  width: 80px;
  height: 80px;
`

const JobOfferContents = styled.section`
  display: flex;
  flex-direction: column;
  gap: .25rem;
  flex-grow: 1;
`

const WorkStyle = styled.div`
  font-size: .75rem;
  font-weight: 700;
  line-height: 1;
  color: ${props => props.theme.members.colors.appleGreen._90};
  border: 1px solid ${props => props.theme.members.colors.appleGreen._90};
  border-radius: 4px;
  padding: .25rem .5rem;
  width: fit-content;
`

const JobOfferTitle = styled.div`
  font-weight: 700;
`

const JobOfferPrice = styled.div`
  display: flex;
  gap: .5rem;
  align-items: center;
  line-height: 30px;
`

const Price = styled.div`
  color: ${props => props.theme.members.colors.red};
  font-weight: 600;
  font-size: 1.25rem;
  font-family: 'Open Sans', sans-serif;
`

const PriceUnit = styled.div`
  font-size: .75rem;
`

const JobOfferFooter = styled.div`
  display: flex;
  gap: .5rem;
  font-size: .75rem;
  color: ${props => props.theme.members.colors.black._60};
  width: 100%;
  flex-direction: column;

  @media ${props => props.theme.media.md} {
    flex-direction: row;
    gap: 1rem;
  }
`

const FooterInfo = styled.div`
  display: flex;
  gap: .5rem;
  align-items: center;
`

const FooterInfoOccupation = styled(FooterInfo)`
  @media ${props => props.theme.media.md} {
    max-width: 60%;
  }
`

const Icon = styled.img`
  width: 1rem;
  height: 1rem;
`

export const StyledJobOfferSummaryCard: React.FC<Props> = props => {
  const { formatNumber } = useFormatNumber()

  return (
    <Container href={`/job_offers/${props.jobOfferSummary.id}`} data-cy="job-offer-summary-card">
      <Image src={GreenWave} />
      <JobOfferContents>
        <WorkStyle>{props.jobOfferSummary.workStyleName}</WorkStyle>
        <JobOfferTitle>{props.jobOfferSummary.title}</JobOfferTitle>
        <JobOfferPrice>
          <Price>~{formatNumber(props.jobOfferSummary.maxUnitPrice)}</Price>
          <PriceUnit>円／月</PriceUnit>
        </JobOfferPrice>
        <JobOfferFooter>
          <FooterInfoOccupation>
            <Icon src={IconOccupation} />
            {props.jobOfferSummary.occupationTitle}
          </FooterInfoOccupation>
          {
            props.jobOfferSummary.officeLocation &&
            <FooterInfo>
              <Icon src={IconArea} />
              {props.jobOfferSummary.officeLocation}駅
            </FooterInfo>
          }
        </JobOfferFooter>
      </JobOfferContents>
    </Container>
  )
}
